import { useToast } from '@/hooks'
import { useProjectMutations } from '@/modules/projects/hooks/project'
import type { Project } from '@/modules/projects/stores'
import type { CaseAnswers } from '@/modules/projects/stores/caseConfig'
import type { ProjectCaseForm } from './types'
import { useCaseAnswers } from './useCaseAnswers'

export interface ProjectCaseEditor extends ProjectCaseForm {
  save: () => Promise<Project>
}

interface ProjectCaseFormOptions {
  saveOnChange?: boolean
}

export const useProjectCaseForm = ({
  saveOnChange = false,
}: ProjectCaseFormOptions = {}): ProjectCaseEditor => {
  const answers = useCaseAnswers()
  const { updateProject } = useProjectMutations()
  const { errorToast } = useToast()

  const save = (next: CaseAnswers) =>
    updateProject({
      case_type: next.caseType,
      case_details: next.caseDetails,
      case_accounts_type: next.caseAccountsType,
    })

  const afterChange = (next: CaseAnswers) => {
    if (!saveOnChange) return
    save(next).catch((error) =>
      errorToast('Couldn’t save the case details', error),
    )
  }

  return {
    ...answers.form,
    onCaseTypeChange: (value) => afterChange(answers.changeCaseType(value)),
    onDetailChange: answers.changeDetail,
    onDetailCommit: saveOnChange
      ? (key, value) => afterChange(answers.changeDetail(key, value))
      : undefined,
    onAccountsChange: (value) => afterChange(answers.changeAccountsType(value)),
    save: () => save(answers.values),
  }
}
